// progress-rows.js — the CRM learner-progress row-shape canon (Brief 025).
//
// Pure + DOM-free + dependency-free. This module is the single place that
// knows what a learner-progress row looks like on the wire; sync-engine.js
// owns WHEN rows move, this owns WHAT they look like.
//
// AUTHORITY: `docs/CRM-LEARNER-PROGRESS-CONFIG-2026-09-14.php` — Allen's CRM
// config, copied verbatim 2026-09-14. Every field list below is that file's
// `fields` entry minus the two columns iOS strips on upload (see
// `stripUploadOnlyFields` below). If Allen updates the config, take a fresh
// copy and delta-check against TABLE_FIELDS.
//
// iOS canon for the envelope + per-row normalisation:
//   KFESyncUserDataMgr.m:92-118   `getUploadPostParameters`  — POST envelope
//   KFESyncUserProgressDataDbMgr.m:154-206 `getNewRecordsWithType` — payload
//   KFESyncUserProgressDataDbMgr.m:258-306 `updateRowDic` → `removeNulls` +
//                                          `removeUnwantedFields`
//   KFESyncUserProgressDataDbMgr.m:638-740 `secondaryTableUpdateStr` — the
//                                          per-column int/string types below
//                                          (`%ld` → int, `%@` → string)
//
// TABLE-NAME NOTE: the CRM's wire names have NO `appserv_` prefix. Earlier
// Brief 025 drafts said otherwise; the config file is the correction.

/**
 * Per-table column type maps, in the CRM config's field ORDER (order matters —
 * Gotcha #1: mirror field names AND order exactly). `"int"` / `"str"` drive
 * the NULL-coercion rule in `normaliseRow`.
 *
 * `id` and `last_updated_on` are absent from every list on purpose: iOS's
 * `removeUnwantedFields` deletes `id`, `last_updated_on` and `UAID` from every
 * uploaded row (the server assigns its own id and stamps its own
 * `updated_at`). Web has no `UAID` to strip — it never sends one at all.
 *
 * @type {Record<string, Record<string, "int"|"str">>}
 */
export const TABLE_FIELDS = {
  app_global_playlist: { row: "int", item_id: "int" },
  app_item_info: {
    item_id: "int", plist_name: "str", items_number: "int",
    items_not_required_number: "int", items_completed_number: "int",
    completed: "int", required_play: "int", add_to_global_playlist: "int",
    item_order: "int", items_number_check: "int",
  },
  app_progress_variables: {
    summary_words_met_last_id_updated: "int", personal_promise_signed: "int",
    username: "str", active_pathway: "str",
  },
  app_review_items: { row: "int", item_id: "int", folder_id: "int" },
  app_TF_combinations: {
    completed: "int", container_item_id: "int", matchbox_item_id: "int",
    mmu_item_id: "int", is_matching: "int",
  },
  learner_l2b_mmus: { mmu_id: "int", consecutive_correct_answers: "int" },
  learner_lesson_items: {
    item_id: "int", time_completed: "int", seconds_spent: "int",
    completed: "int", test_id: "int", time_started: "int", score: "int",
    modality: "str",
  },
  learner_meter_answers: {
    item_id: "int", type: "int", answer1: "int", answer2: "int",
    seconds_spent: "int", at_time: "int",
  },
  learner_test_instances: {
    item_id: "int", item_id2: "int", test_id: "int", mmu_id: "int",
    mmu_id2: "int", attempts: "int", time_started: "int",
    time_completed: "int", pass: "int", type: "int", seconds_spent: "int",
    score: "int", modality: "str",
  },
  summary_daily_use: { date: "int", seconds_spent: "int", activity: "str" },
  summary_lessons_completed: { item_id: "int", at_time: "int" },
  summary_mmus_met: { item_id: "int", mmu_id: "int", at_time: "int", mode: "int" },
  summary_multi_choice_attempts: { item_id: "int", attempts: "int" },
  summary_multi_choice_scores: { item_id: "int", at_time: "int", score: "int", pass: "int" },
  summary_time_spent: { item_id: "int", seconds_spent: "int" },
  summary_words_mastered: { word_id: "int", at_time: "int" },
  // `1st_time` starts with a digit, so the CRM's SELECT aliases it as
  // `'1st_time AS "[1st_time]"'`. That alias is a DOWNLOAD-side artefact; on
  // upload iOS sends the bare column name (`summary_words_met_2 (… , mode,
  // occurrences_number, …)` / the commented-out `[1st_time]` variant). We send
  // `1st_time`. Confirm on the live smoke (Brief 025 §CRM contract #1).
  summary_words_met: {
    item_id: "int", word_id: "int", at_time: "int", mode: "int",
    "1st_time": "int", occurrences_number: "int",
  },
  summary_words_recognition: {
    item_id: "int", word_id: "int", modality: "str", at_time: "int",
  },
};

/**
 * INSERT-only tables (`POST /app/add-learner-progress`). Config
 * `incremental_tables`, minus `app_hiccups` (see WEB_EXCLUDED_TABLES).
 * @type {Set<string>}
 */
export const INCREMENTAL_TABLES = new Set([
  "summary_words_recognition",
  "summary_words_met",
  "summary_multi_choice_scores",
  "summary_time_spent",
  "summary_mmus_met",
  "learner_test_instances",
  "learner_meter_answers",
  // The config comments `summary_words_mastered` as "changed to incremental
  // 7/9/2018" even though it sits in `updatable_tables`; the comment is the
  // later statement of intent, so treat it as incremental (Brief 025
  // §CRM contract #1).
  "summary_words_mastered",
]);

/**
 * Upsert tables (`POST /app/update-learner-progress`). Config
 * `updatable_tables`, minus the iOS-only entries.
 * @type {Set<string>}
 */
export const UPDATABLE_TABLES = new Set([
  "app_progress_variables",
  "summary_lessons_completed",
  "summary_multi_choice_attempts",
  "app_item_info",
  "app_global_playlist",
  "app_review_items",
  "summary_words_mastered",
  "learner_l2b_mmus",
  "summary_daily_use",
  "learner_lesson_items",
  "app_TF_combinations",
  "summary_words_met",
  "summary_time_spent",
]);

/**
 * Tables the web port never pushes and never consumes.
 *   • `milestone_videos` — iOS milestone-recording feature; no web equivalent.
 *     (iOS itself skips it in the sync payload too —
 *     KFESyncUserProgressDataDbMgr.m:165-167 "we don't want the synchroniser
 *     to upload milestone_videos records"; it has its own custom API.)
 *   • `mp_qc` — never syncs down per the config comment; nothing on web writes it.
 *   • `app_hiccups` — iOS-side diagnostic; excluded from the download API and
 *     not needed on web.
 * @type {Set<string>}
 */
export const WEB_EXCLUDED_TABLES = new Set(["milestone_videos", "mp_qc", "app_hiccups"]);

/**
 * `app_progress_variables` is a SINGLETON row, not a list. iOS special-cases it
 * in `getNewRecordsWithType` (KFESyncUserProgressDataDbMgr.m:177-180): the
 * payload value is the row DICT itself, where every other table's value is an
 * ARRAY of row dicts. Mirror that or the server sees a shape it doesn't expect.
 */
export const SINGLETON_TABLES = new Set(["app_progress_variables"]);

/**
 * Which endpoint a table's rows go to.
 *
 * Three tables (`summary_words_met`, `summary_time_spent`,
 * `summary_words_mastered`) appear in BOTH config lists. Per Brief 025
 * §CRM contract #1 the split is by INTENT, not by table: a brand-new row is
 * incremental (`add-`), a retroactive correction to an existing row is an
 * update (`update-`). Callers say which via `isCorrection`.
 *
 * @param {string} table
 * @param {{ isCorrection?: boolean }} [opts]
 * @returns {"add"|"update"}
 */
export function endpointForTable(table, { isCorrection = false } = {}) {
  if (isCorrection && UPDATABLE_TABLES.has(table)) return "update";
  if (INCREMENTAL_TABLES.has(table)) return "add";
  if (UPDATABLE_TABLES.has(table)) return "update";
  throw new Error(`[progress-rows] unknown sync table: ${table}`);
}

/** @param {string} table @returns {boolean} whether web may sync this table at all */
export function isSyncableTable(table) {
  return !WEB_EXCLUDED_TABLES.has(table) && Object.prototype.hasOwnProperty.call(TABLE_FIELDS, table);
}

/**
 * Normalise one row to the wire shape: exactly the table's fields, in the
 * config's order, with iOS's NULL/empty coercion applied.
 *
 * iOS `removeNulls` (KFESyncUserProgressDataDbMgr.m:283-306):
 *   • NULL in an int column  → 0
 *   • NULL in a non-int one  → the STRING "null"
 *   • "" or " "              → the STRING "empty"
 * iOS `removeUnwantedFields` (…:265-281): drop `id`, `last_updated_on`, `UAID`.
 * Both are reproduced here. Fields absent from the caller's row are treated as
 * NULL (→ 0 / "null") rather than omitted, so every row the server sees has
 * the full column set — which is what iOS's whole-SQLite-row dump always sent.
 *
 * Unknown keys on the input row are DROPPED, not forwarded: the field list is
 * the contract, and forwarding a stray key would be a silent schema drift.
 *
 * @param {string} table
 * @param {Record<string, unknown>} row
 * @returns {Record<string, unknown>}
 */
export function normaliseRow(table, row) {
  const fields = TABLE_FIELDS[table];
  if (!fields) throw new Error(`[progress-rows] unknown sync table: ${table}`);
  /** @type {Record<string, unknown>} */
  const out = {};
  for (const [name, type] of Object.entries(fields)) {
    const raw = row[name];
    if (raw === undefined || raw === null) {
      out[name] = type === "int" ? 0 : "null";
      continue;
    }
    if (typeof raw === "string" && raw.trim() === "") {
      out[name] = "empty";
      continue;
    }
    if (type === "int") {
      const n = Number(raw);
      out[name] = Number.isFinite(n) ? Math.trunc(n) : 0;
      continue;
    }
    out[name] = String(raw);
  }
  return out;
}

/**
 * Group already-normalised outbox entries into the `learner_progress` payload.
 * Shape mirrors iOS's `uploadUpdatableDic` / `uploadIncrementalDic`:
 *   { "<table>": [row, row, …], "app_progress_variables": { …single row… } }
 *
 * @param {Array<{ table: string, row: Record<string, unknown> }>} entries
 * @returns {Record<string, unknown>}
 */
export function buildLearnerProgress(entries) {
  /** @type {Record<string, any>} */
  const payload = {};
  for (const { table, row } of entries) {
    const normalised = normaliseRow(table, row);
    if (SINGLETON_TABLES.has(table)) {
      // Last write wins, matching iOS: `singleRowItem` is overwritten on each
      // matching row and only the final one is sent.
      payload[table] = normalised;
      continue;
    }
    (payload[table] ??= []).push(normalised);
  }
  return payload;
}

/**
 * A request id in iOS's format: `unixSeconds * 10000 + rand(0..9999)`
 * (KFESyncUserDataMgr.m:106). The CRM uses it to de-duplicate a retried upload,
 * so a RETRY of the same batch must reuse the id rather than mint a new one —
 * sync-engine persists it on the outbox row for exactly that reason.
 *
 * @param {() => number} [now] injectable clock (ms), for tests
 * @param {() => number} [rand] injectable rng in [0,1), for tests
 * @returns {number}
 */
export function makeRequestId(now = Date.now, rand = Math.random) {
  return Math.floor(now() / 1000) * 10000 + Math.floor(rand() * 10000);
}

/**
 * The full upload POST body.
 *
 * iOS sends `{uaid, request_id, device_time, lang, learner_progress,
 * bundle_version}` (KFESyncUserDataMgr.m:116). Web MUST NOT send `uaid` or
 * `device_time` — every web endpoint states this and crm-client's `request()`
 * never adds them (Brief 188). So the web envelope is iOS's minus those two;
 * the server resolves the learner from the Bearer JWT instead.
 *
 * @param {{ learnerProgress: Record<string, unknown>, requestId: number,
 *           lang?: string, bundleVersion?: string }} params
 * @returns {Record<string, unknown>}
 */
export function buildUploadBody({ learnerProgress, requestId, lang = "en", bundleVersion = "" }) {
  /** @type {Record<string, unknown>} */
  const body = {
    request_id: requestId,
    lang,
    learner_progress: learnerProgress,
  };
  if (bundleVersion) body.bundle_version = bundleVersion;
  return body;
}

// ---------------------------------------------------------------------------
// Download side
// ---------------------------------------------------------------------------

/**
 * Split a SQL-tuple-list string into its individual `(...)` tuples.
 * Quote-aware so a comma or bracket inside a quoted value doesn't split a row.
 * @param {string} text
 * @returns {string[]} each element the inside of one tuple, parens removed
 */
function splitTuples(text) {
  /** @type {string[]} */ const out = [];
  let depth = 0, start = -1, quote = "";
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quote) {
      if (ch === "\\") { i++; continue; }
      if (ch === quote) quote = "";
      continue;
    }
    if (ch === "'" || ch === '"') { quote = ch; continue; }
    if (ch === "(") { if (depth === 0) start = i + 1; depth++; continue; }
    if (ch === ")") { depth--; if (depth === 0 && start >= 0) { out.push(text.slice(start, i)); start = -1; } }
  }
  return out;
}

/** Split one tuple body on top-level commas, quote-aware. @param {string} body */
function splitValues(body) {
  /** @type {string[]} */ const out = [];
  let cur = "", quote = "";
  for (let i = 0; i < body.length; i++) {
    const ch = body[i];
    if (quote) {
      if (ch === "\\") { cur += body[++i] ?? ""; continue; }
      if (ch === quote) { quote = ""; continue; }
      cur += ch;
      continue;
    }
    if (ch === "'" || ch === '"') { quote = ch; continue; }
    if (ch === ",") { out.push(cur.trim()); cur = ""; continue; }
    cur += ch;
  }
  out.push(cur.trim());
  return out;
}

/**
 * Parse the download API's per-table payload into plain row objects.
 *
 * TWO wire shapes supported:
 *
 * 1. **Current CRM (JSON-object rows, verified 2026-09-14 live smoke)** — each
 *    table is `[[ {row_object}, {row_object}, ... ]]`: an outer array whose
 *    elements are chunks (arrays of already-parsed row objects). Field values
 *    are strings (SQLite-native), e.g. `{"id":"9693764","completed":"1"}`.
 *    Merger consumes these directly; no SQL parsing needed.
 *
 * 2. **Legacy SQL-tuple shape** (iOS `KFESyncUserProgressDataDbMgr.m:750-760`) —
 *    `{ fields: "(a,b,c)", values: ["('1','2',NULL)", "..."] }`. Retained as a
 *    fallback in case the server is downgraded or a different endpoint reverts.
 *
 * SQL `NULL` (unquoted or the string "null") becomes JS `null`. Everything
 * else comes back as the string SQLite would have stored; callers coerce per
 * their own use.
 *
 * Tolerant by design: a table whose shape we can't parse yields `[]` rather
 * than throwing, so one malformed table can't abort a whole pull.
 *
 * @param {any} tablePayload
 * @returns {Array<Record<string, string|null>>}
 */
export function parseDownloadedTable(tablePayload) {
  // Shape 1: JSON row objects, wrapped in one or more chunk arrays.
  if (Array.isArray(tablePayload)) {
    /** @type {Array<Record<string, string|null>>} */
    const rows = [];
    for (const chunk of tablePayload) {
      if (Array.isArray(chunk)) {
        for (const row of chunk) {
          const normalised = normaliseJsonRow(row);
          if (normalised) rows.push(normalised);
        }
      } else if (chunk && typeof chunk === "object") {
        const normalised = normaliseJsonRow(chunk);
        if (normalised) rows.push(normalised);
      }
    }
    return rows;
  }

  // Shape 2 (legacy): SQL fields+values tuple string.
  const fieldsRaw = tablePayload?.fields;
  if (typeof fieldsRaw !== "string") return [];
  const names = fieldsRaw.replace(/^\s*\(/, "").replace(/\)\s*$/, "")
    .split(",").map((f) => f.trim().replace(/^["'[`]|["'\]`]$/g, ""));
  if (names.length === 0) return [];

  const chunks = Array.isArray(tablePayload?.values)
    ? tablePayload.values
    : (typeof tablePayload?.values === "string" ? [tablePayload.values] : []);

  /** @type {Array<Record<string, string|null>>} */
  const rows = [];
  for (const chunk of chunks) {
    if (typeof chunk !== "string") continue;
    for (const tuple of splitTuples(chunk)) {
      const vals = splitValues(tuple);
      if (vals.length !== names.length) continue; // shape drift — skip the row
      /** @type {Record<string, string|null>} */ const row = {};
      names.forEach((name, i) => {
        const v = vals[i];
        row[name] = v === "NULL" || v === "null" ? null : v;
      });
      rows.push(row);
    }
  }
  return rows;
}

/**
 * Coerce a JSON row object into the `{ [field]: string | null }` shape the
 * merger expects. Server sends every scalar as a string already (SQLite
 * native), and encodes SQL NULL as JS null OR the literal string `"null"`
 * (seen in `modality` in live smoke).
 * @param {any} row
 * @returns {Record<string, string|null> | null}
 */
function normaliseJsonRow(row) {
  if (!row || typeof row !== "object" || Array.isArray(row)) return null;
  /** @type {Record<string, string|null>} */
  const out = {};
  for (const [k, v] of Object.entries(row)) {
    if (v === null || v === undefined) { out[k] = null; continue; }
    if (typeof v === "string") { out[k] = v === "null" || v === "NULL" ? null : v; continue; }
    out[k] = String(v);
  }
  return out;
}

/**
 * Parse a whole `data.learner_progress` block into `{ table: rows[] }`,
 * skipping the tables web never consumes.
 * @param {Record<string, any> | undefined} learnerProgress
 * @returns {Record<string, Array<Record<string, string|null>>>}
 */
export function parseDownloadedProgress(learnerProgress) {
  /** @type {Record<string, Array<Record<string, string|null>>>} */
  const out = {};
  if (!learnerProgress || typeof learnerProgress !== "object") return out;
  for (const [table, payload] of Object.entries(learnerProgress)) {
    if (WEB_EXCLUDED_TABLES.has(table)) continue;
    const rows = parseDownloadedTable(payload);
    if (rows.length > 0) out[table] = rows;
  }
  return out;
}
